import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import Sidebar from "./Sidebar";
import { Outlet } from "react-router";
import { useTheme } from "@mui/material";
import { useMedia } from "../context/MediaContext";
import Header from "./Header";
import MobileSlider from "./MobileSlider";

const Layout: React.FC<{
  isDark: boolean;
  setIsDark: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ isDark, setIsDark }) => {
  const { isTablet } = useMedia();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const updateHeights = () => {
      const vvh = window.visualViewport?.height ?? window.innerHeight;

      document.documentElement.style.setProperty("--vvh", `${vvh}px`);

      document.documentElement.style.setProperty(
        "--content-max-height",
        `calc(
        ${vvh}px * 0.62
        - env(safe-area-inset-top)
        - env(safe-area-inset-bottom)
      )`,
      );
    };

    updateHeights();

    window.visualViewport?.addEventListener("resize", updateHeights);
    window.addEventListener("resize", updateHeights);

    return () => {
      window.visualViewport?.removeEventListener("resize", updateHeights);
      window.removeEventListener("resize", updateHeights);
    };
  }, []);

  return (
    <Box
      sx={{
        // A fixed height, not just a minimum: with minHeight alone the root grew
        // to fit its content (2267px inside a 900px window) while body has
        // overflow: hidden, so the bottom of the page — pagination included —
        // could not be reached.
        height: "var(--vvh)",
        minHeight: "var(--vvh)",
        overflow: "hidden",
        width: "100%",
        background:
          theme.palette.mode === "dark"
            ? "radial-gradient(circle at 12% -8%, rgba(99, 91, 255, 0.34), transparent 31%), linear-gradient(145deg, #10172b 0%, #0b1020 62%, #12182a 100%)"
            : "radial-gradient(circle at 12% -8%, rgba(139, 132, 255, 0.28), transparent 31%), linear-gradient(145deg, #fafbff 0%, #eef1f8 58%, #f7f8fc 100%)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        paddingBottom: { xs: 1, md: 2 },
      }}
    >
      <Header isDark={isDark} setIsDark={setIsDark} />
      <Box
        sx={{
          width: { xs: "calc(100% - 16px)", md: "calc(100% - 40px)" },
          maxWidth: "1680px",
          display: "flex",
          margin: "0 auto",
          gap: { xs: 0, md: 1.5 },
          flex: 1,
          minHeight: 0,
        }}
      >
        <Sidebar open={open} setOpen={setOpen} isMobile={isTablet} />
        <MobileSlider open={open} setOpen={setOpen} isMobile={isTablet} />
        <Box
          sx={{
            width: isTablet ? "100%" : "calc(100% - 80px)",
            flex: 1,
            backgroundColor: theme.palette.background.default,
            borderRadius: { xs: "20px", md: "26px" },
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 24px 60px rgba(0, 0, 0, 0.3)"
                : "0 24px 60px rgba(33, 43, 75, 0.12)",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            zIndex: 0,
          }}
        >
          <Outlet />

          {open && isTablet && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(8, 12, 24, 0.54)",
                backdropFilter: "blur(2px)",
                zIndex: 10,
              }}
              onClick={() => setOpen(false)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
